import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, MessageCircle, CheckCircle2, AlertTriangle, Plug, FlaskConical, Plus, Pencil, Trash2, Copy } from "lucide-react";
import api, { errMsg } from "../../lib/api";
import SEO from "../../components/SEO";
import { TextF, SelectF, StatusBadge, inputCls } from "../admin/AFields";
import { Modal } from "../admin/Participants";

const TBLANK = { name: "", campaign_name: "", category: "UTILITY", language: "en", params: "name", status: "pending" };
const CBLANK = { provider: "twilio", api_key: "", default_campaign: "", test_number: "", twilio_account_sid: "", twilio_auth_token: "", twilio_whatsapp_from: "" };

const STATE_STYLE = {
  "NOT CONFIGURED": "bg-slate-100 text-slate-600",
  "ACTION REQUIRED": "bg-saffron-100 text-saffron-600",
  "CONNECTED": "bg-brandgreen-100 text-brandgreen",
  "ACTIVE": "bg-brandgreen-100 text-brandgreen",
  "ERROR": "bg-red-50 text-red-500",
};

const GUIDES = {
  aisensy: "One-time setup: in AiSensy go to Campaigns → +Launch → API Campaign (pick your approved template, set Live), then Manage → API Key → Copy. Paste the key here — it stays on the server only.",
  twilio: "One-time setup: in Twilio Console copy your Account SID + Auth Token, register your WhatsApp sender (Messaging → Senders), and create an approved Content Template (Content SID starts with HX…). Everything stays server-side only.",
};

export default function StudioWhatsApp() {
  const [status, setStatus] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [connecting, setConnecting] = useState(false);
  const [conn, setConn] = useState(CBLANK);
  const [check, setCheck] = useState(null);
  const [editing, setEditing] = useState(null);
  const [testPhone, setTestPhone] = useState("");
  const [testTemplate, setTestTemplate] = useState("");
  const [testResult, setTestResult] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () => {
    api.get("/wa/status").then((r) => { setStatus(r.data); setTestPhone((p) => p || r.data.test_number || ""); }).catch((e) => toast.error(errMsg(e)));
    api.get("/wa/templates").then((r) => setTemplates(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const connect = async () => {
    setBusy("connect");
    try {
      const r = await api.post("/wa/connect", conn);
      toast.success(`Saved — status: ${r.data.state}`);
      setConnecting(false); setConn(CBLANK); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect WhatsApp? Saved credentials will be removed.")) return;
    try { await api.post("/wa/disconnect"); toast.success("Disconnected — status NOT CONFIGURED"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const testConnection = async () => {
    setBusy("testconn"); setCheck(null);
    try {
      const r = await api.post("/wa/test-connection");
      setCheck(r.data);
      if (r.data.result === "WHATSAPP READY") toast.success("WHATSAPP READY"); else toast.warning("ACTION REQUIRED — see checks");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  const sendTest = async () => {
    setBusy("testmsg"); setTestResult(null);
    try {
      await api.post("/wa/test-message", { phone: testPhone, template_id: testTemplate || undefined });
      setTestResult({ ok: true });
      toast.success("Test message accepted by the provider — check the phone");
      load();
    } catch (e) { setTestResult({ ok: false, msg: errMsg(e) }); toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  const saveTemplate = async () => {
    try { await api.post("/wa/templates/save", editing); toast.success("Template saved"); setEditing(null); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const removeTemplate = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try { await api.delete(`/wa/templates/${t.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const state = status?.state || "NOT CONFIGURED";
  const provider = status?.provider || "twilio";
  const isTwilio = provider === "twilio";
  const approved = templates.filter((t) => t.status === "approved");

  const openConnect = () => {
    setConn({ ...CBLANK, provider, default_campaign: status?.default_campaign || "",
              test_number: status?.test_number || "", twilio_account_sid: status?.twilio_account_sid || "",
              twilio_whatsapp_from: status?.twilio_whatsapp_from || "" });
    setConnecting(true);
  };

  return (
    <div data-testid="studio-whatsapp">
      <SEO title="WhatsApp" />
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl">WhatsApp ({isTwilio ? "Twilio" : "AiSensy"})</h1>
          <p className="mt-1 text-sm text-slate-500">Official WhatsApp Business Platform — Twilio (primary) or AiSensy. No unofficial automation — ever.</p>
        </div>
        <span className={`rounded-full px-4 py-1.5 text-xs font-bold ${STATE_STYLE[state] || STATE_STYLE["NOT CONFIGURED"]}`} data-testid="wa-state">{state}</span>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <div className="space-y-6">
          <div className="rounded-lg border border-gray-200 bg-white p-6" data-testid="wa-connect-card">
            <h2 className="flex items-center gap-2 font-display text-lg font-bold text-navy"><Plug size={17} className="text-saffron-600" /> Connection</h2>
            <p className="mt-1 text-xs text-slate-500">{GUIDES[provider]}</p>
            <div className="mt-4 flex flex-wrap gap-2">
              <button onClick={openConnect} className="btn-primary !rounded-md !px-5 !py-2.5 text-sm" data-testid="wa-connect-btn"><MessageCircle size={15} /> Connect WhatsApp</button>
              <button onClick={testConnection} disabled={busy === "testconn"} className="btn-dark !rounded-md !px-5 !py-2.5 text-sm disabled:opacity-50" data-testid="wa-test-connection">
                {busy === "testconn" ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Test Connection
              </button>
              {state !== "NOT CONFIGURED" && (
                <button onClick={disconnect} className="rounded-md border border-red-200 px-4 py-2.5 text-sm font-semibold text-red-500" data-testid="wa-disconnect-btn">Disconnect</button>
              )}
            </div>
            {check && (
              <div className={`mt-4 rounded-lg px-4 py-3 ${check.result === "WHATSAPP READY" ? "bg-brandgreen-100" : "bg-saffron-100"}`} data-testid="wa-check-result">
                <p className={`text-sm font-bold ${check.result === "WHATSAPP READY" ? "text-brandgreen" : "text-saffron-600"}`}>{check.result}</p>
                <ul className="mt-2 space-y-1 text-xs font-semibold text-slate-600">
                  {Object.entries(check.checks).map(([k, v]) => (
                    <li key={k} className="flex items-center gap-2">{v ? <CheckCircle2 size={13} className="text-brandgreen" /> : <AlertTriangle size={13} className="text-red-500" />} {k.replace(/_/g, " ")}</li>
                  ))}
                </ul>
                {check.note && <p className="mt-2 text-xs text-slate-500">{check.note}</p>}
              </div>
            )}
            {status?.webhook_url && (
              <div className="mt-4 rounded-md bg-navy-50 p-4">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Delivery Webhook ({isTwilio ? "Twilio Status Callback" : "AiSensy/Meta dashboard"})</p>
                <div className="mt-2 flex items-center gap-2">
                  <code className="flex-1 break-all rounded bg-white px-3 py-2 text-xs text-navy" data-testid="wa-webhook-url">{status.webhook_url}</code>
                  <button onClick={() => { navigator.clipboard.writeText(status.webhook_url); toast.success("Copied"); }} className="rounded-md border border-gray-200 bg-white p-2 text-navy" aria-label="Copy webhook URL" data-testid="wa-webhook-copy"><Copy size={14} /></button>
                </div>
                {!isTwilio && <p className="mt-2 text-xs text-slate-400">Verify token: <code className="rounded bg-white px-1.5 py-0.5">{status.verify_token || "created on first save"}</code></p>}
                {isTwilio && <p className="mt-2 text-xs text-slate-400">Twilio callbacks are verified automatically with your Auth Token signature — nothing else to configure here.</p>}
              </div>
            )}
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-6" data-testid="wa-test-card">
            <h2 className="flex items-center gap-2 font-display text-lg font-bold text-navy"><FlaskConical size={17} className="text-saffron-600" /> Send Test Message</h2>
            <p className="mt-1 text-xs text-slate-500">Uses the exact same production pipeline as a real campaign.{isTwilio ? " Trial accounts can only message verified numbers." : ""}</p>
            <div className="mt-4 space-y-4">
              <TextF label="Test Number (with country code)" value={testPhone} onChange={setTestPhone} placeholder="91XXXXXXXXXX" />
              <div>
                <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">Template (approved)</label>
                <select value={testTemplate} onChange={(e) => setTestTemplate(e.target.value)} className={inputCls} data-testid="wa-test-template">
                  <option value="">Default {isTwilio ? "Content Template" : "API campaign"}</option>
                  {approved.map((t) => <option key={t.id} value={t.id}>{t.name} ({t.campaign_name})</option>)}
                </select>
              </div>
              <button onClick={sendTest} disabled={busy === "testmsg" || state === "NOT CONFIGURED"} className="btn-primary !rounded-md !px-5 !py-2.5 text-sm disabled:opacity-50" data-testid="wa-send-test">
                {busy === "testmsg" ? <Loader2 size={14} className="animate-spin" /> : <MessageCircle size={14} />} Send Test Message
              </button>
              {testResult && !testResult.ok && <p className="rounded-lg bg-red-50 px-4 py-2.5 text-xs font-semibold text-red-600" data-testid="wa-test-error">{testResult.msg}</p>}
              {testResult?.ok && <p className="rounded-lg bg-brandgreen-100 px-4 py-2.5 text-xs font-bold text-brandgreen" data-testid="wa-test-ok">Message accepted — delivery status will update via webhook.</p>}
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-6" data-testid="wa-templates-card">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-lg font-bold text-navy">WhatsApp Templates</h2>
            <button onClick={() => setEditing({ ...TBLANK })} className="btn-dark !rounded-md !px-4 !py-2 text-xs" data-testid="wa-template-new"><Plus size={13} /> Register Template</button>
          </div>
          <p className="mt-1 text-xs text-slate-500">{isTwilio
            ? "Register each approved Twilio Content Template with its Content SID (HX…). Campaigns can only use APPROVED templates."
            : "Register each template exactly as approved in AiSensy. Campaigns can only use APPROVED templates — we never bypass template approval."}</p>
          <div className="mt-4 space-y-3">
            {templates.map((t) => (
              <div key={t.id} className="rounded-md border border-gray-100 p-4" data-testid={`wa-tpl-${t.id}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-bold text-navy">{t.name}</p>
                    <p className="mt-0.5 text-xs text-slate-500">{isTwilio ? "content sid" : "campaign"}: {t.campaign_name} · {t.language} · vars: {(t.params || []).join(", ")}</p>
                    <p className="text-[10px] text-slate-400">updated {String(t.updated_at || "").slice(0, 10)}</p>
                  </div>
                  <div className="flex shrink-0 items-center gap-1.5">
                    <StatusBadge value={t.status} />
                    <button onClick={() => setEditing({ ...TBLANK, ...t, params: (t.params || []).join(", ") })} className="rounded-md border border-gray-200 p-1.5 text-navy hover:border-saffron-500" aria-label="Edit" data-testid={`wa-tpl-edit-${t.id}`}><Pencil size={13} /></button>
                    <button onClick={() => removeTemplate(t)} className="rounded-md border border-gray-200 p-1.5 text-red-500 hover:border-red-400" aria-label="Delete" data-testid={`wa-tpl-del-${t.id}`}><Trash2 size={13} /></button>
                  </div>
                </div>
              </div>
            ))}
            {templates.length === 0 && <p className="py-8 text-center text-sm text-slate-400">No templates registered. Add your approved {isTwilio ? "Twilio Content Template" : "AiSensy template"} here.</p>}
          </div>
        </div>
      </div>

      {connecting && (
        <Modal onClose={() => setConnecting(false)} title="Connect WhatsApp" testid="wa-connect-modal">
          <div className="space-y-5">
            <div className="rounded-md bg-navy-50 px-4 py-3 text-xs font-semibold leading-relaxed text-navy" data-testid="wa-routing-note">Automatic routing: <b>Twilio</b> sends individual/transactional messages · <b>AiSensy</b> sends broadcast campaigns. Configure whichever you use below — server-side environment credentials (TWILIO_* / AISENSY_*) are applied automatically when a field is left empty.</div>
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Twilio — individual / transactional</p>
            <TextF label="Twilio Account SID (AC…)" value={conn.twilio_account_sid} onChange={(v) => setConn({ ...conn, twilio_account_sid: v })} />
            <div>
              <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">Twilio Auth Token</label>
              <input type="password" value={conn.twilio_auth_token} onChange={(e) => setConn({ ...conn, twilio_auth_token: e.target.value })} className={inputCls} autoComplete="new-password" data-testid="wa-twilio-token" placeholder="From Twilio Console — stored server-side only" />
            </div>
            <TextF label="WhatsApp Sender Number" value={conn.twilio_whatsapp_from} onChange={(v) => setConn({ ...conn, twilio_whatsapp_from: v })} placeholder="whatsapp:+14155238886 or +91…" />
            <TextF label="Default Content Template SID (HX…)" value={conn.default_campaign} onChange={(v) => setConn({ ...conn, default_campaign: v })} />
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500">AiSensy — broadcast campaigns</p>
            <div>
              <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">AiSensy API Key</label>
              <input type="password" value={conn.api_key} onChange={(e) => setConn({ ...conn, api_key: e.target.value })} className={inputCls} autoComplete="new-password" data-testid="wa-api-key" placeholder="Paste from AiSensy → Manage → API Key" />
            </div>
            <TextF label="Your Test WhatsApp Number" value={conn.test_number} onChange={(v) => setConn({ ...conn, test_number: v })} placeholder="91XXXXXXXXXX" />
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <button onClick={() => setConnecting(false)} className="rounded-md border border-gray-200 px-5 py-2.5 text-sm font-semibold text-navy">Cancel</button>
            <button onClick={connect} disabled={busy === "connect"} className="btn-primary !rounded-md !px-6 !py-2.5 text-sm disabled:opacity-50" data-testid="wa-connect-save">
              {busy === "connect" && <Loader2 size={14} className="animate-spin" />} Save Securely
            </button>
          </div>
        </Modal>
      )}

      {editing && (
        <Modal onClose={() => setEditing(null)} title="WhatsApp Template" testid="wa-template-editor">
          <div className="space-y-5">
            <TextF label="Template Name" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} required />
            <TextF label={isTwilio ? "Twilio Content Template SID (HX…)" : "AiSensy API Campaign Name (exact)"} value={editing.campaign_name} onChange={(v) => setEditing({ ...editing, campaign_name: v })} />
            <div className="grid gap-5 sm:grid-cols-2">
              <SelectF label="Category" value={editing.category} onChange={(v) => setEditing({ ...editing, category: v })} options={["UTILITY", "MARKETING", "AUTHENTICATION"]} />
              <SelectF label="Language" value={editing.language} onChange={(v) => setEditing({ ...editing, language: v })} options={["en", "en_US", "hi", "mr"]} />
            </div>
            <TextF label="Variables in order (comma separated)" value={editing.params} onChange={(v) => setEditing({ ...editing, params: v })} placeholder="name, occasion, organization" />
            <SelectF label="Provider Status" value={editing.status} onChange={(v) => setEditing({ ...editing, status: v })} options={["draft", "pending", "approved", "rejected"]} />
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <button onClick={() => setEditing(null)} className="rounded-md border border-gray-200 px-5 py-2.5 text-sm font-semibold text-navy">Cancel</button>
            <button onClick={saveTemplate} className="btn-primary !rounded-md !px-6 !py-2.5 text-sm" data-testid="wa-template-save">Save</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
